import React, { useEffect, useState } from 'react';
import { userNotificationsApi, handleNotificationError } from '../utils/notificationsApi';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';

function UserNotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  const loadNotifications = async (pageNum = page) => {
    try {
      setLoading(true);
      const res = await userNotificationsApi.list(pageNum, limit);
      if (res.success) {
        setNotifications(res.data || res.notifications || []);
        setTotal(res.pagination?.total || 0);
        setPage(pageNum);
      }
    } catch (error) {
      handleNotificationError(error, 'Bildirimler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMarkRead = async (id) => {
    try {
      await userNotificationsApi.markRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      );
    } catch (error) {
      handleNotificationError(error, 'Bildirim okunamadı');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      setMarkingAll(true);
      await userNotificationsApi.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (error) {
      handleNotificationError(error, 'Bildirimler okundu işaretlenemedi');
    } finally {
      setMarkingAll(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="container py-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <div>
          <h5 className="mb-1">Bildirimlerim</h5>
          <p className="text-muted mb-0">Size gelen bildirimleri görüntüleyin</p>
        </div>
        <div className="d-flex gap-2 mt-3 mt-md-0">
          <button
            className="btn btn-outline-secondary"
            onClick={() => loadNotifications(1)}
            disabled={loading}
          >
            <i className={`fas fa-sync ${loading ? 'fa-spin' : ''} me-2`}></i>
            Yenile
          </button>
          <button
            className="btn btn-orange"
            onClick={handleMarkAllRead}
            disabled={markingAll || loading}
          >
            {markingAll ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                İşleniyor...
              </>
            ) : (
              <>
                <i className="fas fa-check-double me-2"></i>
                Tümünü okundu yap
              </>
            )}
          </button>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-orange" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center text-muted">
              <i className="fas fa-bell-slash fa-2x mb-2 text-secondary"></i>
              <p className="mb-0">Henüz bildiriminiz bulunmuyor.</p>
            </div>
          ) : (
            <ul className="list-group list-group-flush">
              {notifications.map((n) => (
                <li key={n.id} className={`list-group-item d-flex justify-content-between align-items-start ${!n.isRead ? 'bg-light' : ''}`}>
                  <div className="me-3">
                    <div className="fw-semibold">{n.title || n.message || 'Bildirim'}</div>
                    {n.message && <div className="text-muted small">{n.message}</div>}
                    <div className="text-muted small">
                      {n.createdAt
                        ? formatDistanceToNow(new Date(n.createdAt), { addSuffix: true, locale: tr })
                        : '-'}
                    </div>
                  </div>
                  <div className="d-flex gap-2 align-items-center">
                    {!n.isRead && (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleMarkRead(n.id)}
                      >
                        <i className="fas fa-check me-1"></i>
                        Okundu
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        {notifications.length > 0 && (
          <div className="card-footer d-flex justify-content-between align-items-center">
            <div className="text-muted small">
              Toplam {total} bildirim
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-light"
                onClick={() => loadNotifications(Math.max(1, page - 1))}
                disabled={page <= 1 || loading}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <span className="small align-self-center">
                {page} / {totalPages}
              </span>
              <button
                className="btn btn-sm btn-light"
                onClick={() => loadNotifications(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages || loading}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserNotificationsPage;

